import React from 'react';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { patchSessionPsychiatryNote, sessionParticipantOptionsDict, sessionServiceOptionsDict } from '@uplift/api';
import { L10nHTML, useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';
import { useAppointmentByIdQuery, useSessionPsychiatryNoteQuery, useSessionQuery } from '@uplift/queries';
import { Select, SelectProps, TIME_RANGE_DATE_VALUE_FORMAT, TimeRangeInput, getStartAndEndTimeInput } from '../../../Inputs';
import { FormikAutoSave } from '../../../ServerForm';
import { NestedFormik } from '../../../Form';
import { PsychiatryNoteSection } from '../components/PsychiatryNoteSection.component';


const SessionServiceSelect: React.VFC<SelectProps> = (props) => {
  return (
    <Select
      {...props}
      items={[
        { label: sessionServiceOptionsDict.audio_and_video, value: 'audio_and_video' },
        { label: sessionServiceOptionsDict.in_person, value: 'in_person' }
      ]}
    />
  );
};


const SessionParticipantSelect: React.VFC<SelectProps> = (props) => {
  return (
    <Select
      {...props}
      items={[
        { label: sessionParticipantOptionsDict.client_only, value: 'client_only' },
        { label: sessionParticipantOptionsDict.client_and_family_member, value: 'client_and_family_member' },
        { label: sessionParticipantOptionsDict.family_member, value: 'family_member' }
      ]}
    />
  );
};


export type DetailsSectionProps = {
  appointmentId: string;
  sessionId: string;
};


export const DetailsSection: React.FC<DetailsSectionProps> = (props) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const queryClient = useQueryClient();
  const { data: appointment } = useAppointmentByIdQuery(props.appointmentId, { suspense: true });
  const { data: session } = useSessionQuery(props.sessionId, { suspense: true });
  const { data: psychiatryNote } = useSessionPsychiatryNoteQuery(props.sessionId, { suspense: true, cacheTime: 0 });

  const initialValues = React.useMemo(() => {
    const startTime = psychiatryNote?.startTime ?? appointment?.fromDatetime;
    const endTime = psychiatryNote?.endTime ?? appointment?.toDatetime;

    return {
      participation: session?.participation,
      serviceType: session?.serviceType,
      sessionDuration: {
        start: CalendarDateService.date(startTime).tz(timezone).format(TIME_RANGE_DATE_VALUE_FORMAT),
        end: CalendarDateService.date(endTime).tz(timezone).format(TIME_RANGE_DATE_VALUE_FORMAT),
      }
    };
  }, [ psychiatryNote, appointment, session, timezone ]);

  return (
    <PsychiatryNoteSection title="Details">
      <NestedFormik
        name="details"
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          const range = getStartAndEndTimeInput(appointment?.fromDatetime ?? '', timezone, values.sessionDuration.start, values.sessionDuration.end);

          await patchSessionPsychiatryNote(props.sessionId, {
            participation: values.participation,
            service_type: values.serviceType,
            start_time: range.startTime,
            end_time: range.endTime,
          });

          await Promise.all([
            queryClient.invalidateQueries([ 'session', session?.id ]),
            queryClient.invalidateQueries([ 'appointment', appointment?.id, 'session' ]),
          ]);
        }}
      >
        <Box
          px={3}
          title={`Session ID: ${props.sessionId}`}
          display="flex"
          flexDirection="column"
          gap="16px"
        >
          <SessionParticipantSelect name="participation" label={l10n.clinicalNotes.participants} />
          <SessionServiceSelect name="serviceType" label={l10n.clinicalNotes.location} />
          <TimeRangeInput
            startTitle={l10n.clinicalNotes.startTime}
            endTitle={l10n.clinicalNotes.endTime}
            name="sessionDuration"
            searchSelect
            endTimeLaterThanStartTime={false}
            endTimeLaterThanStartTimeAsTooltip
            endTimeLaterThanStartTimeMessage={l10n.clinicalNotes.validations.endTimeAfterStartTime}
            lessThenHourWarningTitle=""
            minuteIncrement={5}
            helperText={<L10nHTML variant="body2">{l10n.clinicalNotes.timeShouldReflect}</L10nHTML>}
            disableInclusiveEnd
          />
          <FormikAutoSave />
        </Box>
      </NestedFormik>
    </PsychiatryNoteSection>
  );
};
